import { useEffect, useState } from 'react'
import {
    CONTINUE_VERIFICATION_EVENT,
    FAILED_VERIFICATION_EVENT,
    PASSED_VERIFICATION_EVENT,
    VERIFY_IDENTITY_EVENT,
} from '../../analytics/events'
import { ScriptStatus } from '../../utils/useScript'
import { useAnalytics } from '../../context/useAnalytics'
import {
    useKycStatusSetUserCompletedMutation,
    useVerificationDataQuery,
} from '../../graphql/generated/graphql'
import { config } from '../app/config'
import { logger } from '../app/logger'
import { useVerificationContext, VerificationStatus } from './VerificationContext'
import { CloseEvent, OpenEvent, SessionEvent, StepEvent } from './types'
import { useCognitoFlowScript } from './useCognitoFlowScript'

const useSetUserCompleted = () => {
    const { refetch } = useVerificationDataQuery()
    const [setUserCompleted] = useKycStatusSetUserCompletedMutation({
        update(cache, { data }) {
            refetch()
        },
    })

    return setUserCompleted
}

export const useIdentityVerification = () => {
    const { trackAnalyticsEvent } = useAnalytics()
    const { data, refetch } = useVerificationDataQuery()
    const { verificationStatus, billPayStatus, isKYCEnabled, loading } = useVerificationContext()
    const isActive = verificationStatus == VerificationStatus.Active
    const setUserCompleted = useSetUserCompleted()
    const [flow, setFlow] = useState<any>(null)
    const [flowLoading, setFlowLoading] = useState<boolean>(false)
    const [shouldStart, setShouldStart] = useState<boolean>(false)
    const [closed, setClosed] = useState<boolean>(false)
    const scriptStatus = useCognitoFlowScript(shouldStart)

    useEffect(() => {
        if (flow) return
        if (scriptStatus !== ScriptStatus.ready) return

        const _flow =
            //@ts-ignore
            new window.Flow({
                ...config.cognito,
                user: {
                    customerReference: data?.me.id,
                    email: data?.me.email,
                    signature: data?.me.kycStatus.signature,
                },
            })

        setFlow(_flow)

        // Receive events about when the Flow UI opens and closes
        _flow.on('ui', (event: OpenEvent | CloseEvent) => {
            if (event.action === 'closed') {
                console.log('Modal closed')
                setClosed(true)
                refetch()
            } else {
                console.log('Modal opened')
            }
            setFlowLoading(false)
        })
        // Receive events about errors launching flow
        _flow.on('errors', (event: ErrorEvent) => {
            logger.error(`Cognito Flow error! ${event.message}`)
            setFlowLoading(false)
        })

        _flow.on('session', (event: SessionEvent) => {
            switch (event.action) {
                case 'created':
                    console.log('User started a new flow session')
                    break

                case 'resumed':
                    console.log('User resumed an existing flow session')
                    break

                case 'passed':
                    console.log('User successfully completed their flow session')
                    trackAnalyticsEvent(PASSED_VERIFICATION_EVENT)
                    setUserCompleted()
                    break

                case 'failed':
                    console.log('User failed their flow session')
                    trackAnalyticsEvent(FAILED_VERIFICATION_EVENT)
                    setUserCompleted()
                    break
            }
        })

        _flow.on('step', (event: StepEvent) => {
            console.log(`Step`, { event })
        })

        _flow.open()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scriptStatus, data, flow])

    const startVerification = () => {
        if (shouldStart) return
        setFlowLoading(true)
        setShouldStart(true)
        trackAnalyticsEvent(isActive ? CONTINUE_VERIFICATION_EVENT : VERIFY_IDENTITY_EVENT)
    }

    return {
        enabled: isKYCEnabled,
        isActive,
        isVerified: verificationStatus === VerificationStatus.Verified,
        startVerification,
        loading,
        flowLoading,
        verificationStatus,
        billPayStatus,
        flowClosed: closed,
    }
}
